// theme.ts — Wave 14de
// =====================
//
// 3-Wege-Theme-Preference: 'light' (Default — User-Wunsch wegen Lesbarkeit
// in der Sonne), 'dark', 'system' (folgt der OS-Einstellung).
//
// Wie funktionierts:
//   - Preference in localStorage unter `adt_theme`.
//   - Bei 'system' lauschen wir auf matchMedia('(prefers-color-scheme: dark)')
//     und passen die effective an, wenn der User OS-Theme wechselt.
//   - Effective ('light' | 'dark') wird als CSS-Klasse auf <html> gesetzt:
//       html.light  oder  html.dark
//   - Komponenten brauchen NICHTS zu ändern — die CSS-Override-Layer in
//     styles/light-theme.css flippt die häufigsten Tailwind-dark-Klassen
//     für html.light auf Light-Varianten.
//
// Beim ersten Aufruf wird das HTML sofort synchron applied (im
// `applyInitialTheme()` aus main.tsx) damit es keinen Flash gibt.

import { useEffect, useState } from 'react';
import { usePreference } from './usePreference';

export const THEME_PREF_KEY = 'adt_theme';
export type ThemePref = 'light' | 'dark' | 'system';
export type EffectiveTheme = 'light' | 'dark';

const VALID_PREFS: ThemePref[] = ['light', 'dark', 'system'];

function normalizePref(raw: string | null | undefined): ThemePref {
  if (raw && (VALID_PREFS as string[]).includes(raw)) return raw as ThemePref;
  return 'light';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function effectiveFor(pref: ThemePref): EffectiveTheme {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref;
}

function applyHtmlClass(effective: EffectiveTheme) {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  if (effective === 'dark') {
    html.classList.add('dark');
    html.classList.remove('light');
  } else {
    html.classList.add('light');
    html.classList.remove('dark');
  }
}

/**
 * Wird einmal beim App-Boot synchron aufgerufen (vor erstem React-Render)
 * damit der User keinen kurzen Flash der falschen Farbe sieht.
 */
export function applyInitialTheme() {
  if (typeof window === 'undefined') return;
  const raw = window.localStorage?.getItem(THEME_PREF_KEY);
  const pref = normalizePref(raw);
  applyHtmlClass(effectiveFor(pref));
}

/**
 * Hook: liefert die aktuelle Preference + Setter. Wendet bei Änderung den
 * effective sofort auf <html> an. Lauscht außerdem auf OS-Theme-Wechsel
 * wenn pref='system' ist.
 */
export function useThemePref(): {
  pref: ThemePref;
  effective: EffectiveTheme;
  setPref: (next: ThemePref) => void;
} {
  const [rawPref, setRawPref] = usePreference(THEME_PREF_KEY, 'light');
  const pref = normalizePref(rawPref);
  const [effective, setEffective] = useState<EffectiveTheme>(() =>
    effectiveFor(pref),
  );

  // Pref-Wechsel → effective + html.class neu berechnen.
  useEffect(() => {
    const eff = effectiveFor(pref);
    setEffective(eff);
    applyHtmlClass(eff);
  }, [pref]);

  // 'system'-Modus: matchMedia lauschen damit OS-Theme-Wechsel live wirkt.
  useEffect(() => {
    if (pref !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      const eff: EffectiveTheme = e.matches ? 'dark' : 'light';
      setEffective(eff);
      applyHtmlClass(eff);
    };
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, [pref]);

  return {
    pref,
    effective,
    setPref: (next: ThemePref) => setRawPref(next),
  };
}
